import { NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  LayoutDashboard,
  GraduationCap,
  BookOpen,
  Library,
  User,
  LogOut,
  Sun,
  Moon,
} from 'lucide-react'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { hasAccessToRoute } from '@/shared/config/permissions'
import { cn } from '@/lib/utils'

interface NavItem {
  path: string
  labelKey: string
  icon: React.ComponentType<{ className?: string }>
}

interface NavSection {
  titleKey?: string
  items: NavItem[]
}

const NAV_SECTIONS: NavSection[] = [
  {
    items: [
      { path: '/dashboard', labelKey: 'navigation.home', icon: LayoutDashboard },
    ],
  },
  {
    titleKey: 'formateurNav.pedagogy',
    items: [
      { path: '/etudiants', labelKey: 'navigation.students', icon: GraduationCap },
      { path: '/programmes', labelKey: 'navigation.programs', icon: BookOpen },
      { path: '/administration', labelKey: 'navigation.administration', icon: Library },
    ],
  },
  {
    titleKey: 'formateurNav.account',
    items: [
      { path: '/profil', labelKey: 'navigation.profile', icon: User },
    ],
  },
]

interface SidebarContentProps {
  onNavigate?: () => void
  isDark?: boolean
  onToggleDark?: () => void
}

export function SidebarContent({ onNavigate, isDark, onToggleDark }: SidebarContentProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <div className="flex h-full flex-col">
      {/* Navigation */}
      <nav className="flex-1 space-y-1 p-3 pt-4">
        {NAV_SECTIONS.map((section, sectionIdx) => {
          const visibleItems = section.items.filter((item) =>
            hasAccessToRoute(user?.role, item.path),
          )
          if (visibleItems.length === 0) return null

          return (
            <div key={sectionIdx}>
              {section.titleKey && (
                <div className="px-3 pt-4 pb-1">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-300">
                    {t(section.titleKey)}
                  </p>
                </div>
              )}
              {visibleItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all',
                      isActive
                        ? 'bg-gradient-to-r from-primary/12 to-primary/6 text-primary shadow-sm'
                        : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900',
                    )
                  }
                >
                  <item.icon className="h-5 w-5 shrink-0" />
                  <span>{t(item.labelKey)}</span>
                </NavLink>
              ))}
            </div>
          )
        })}
      </nav>

      {/* Bottom section */}
      <div className="border-t border-neutral-200 p-3 space-y-2">
        {/* Dark mode toggle */}
        {onToggleDark && (
          <button
            onClick={onToggleDark}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-neutral-600 transition-all hover:bg-neutral-100 hover:text-neutral-900"
          >
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            <span>{isDark ? t('adminNav.lightMode', 'Mode clair') : t('adminNav.darkMode', 'Mode sombre')}</span>
          </button>
        )}
        {/* User info */}
        {user && (
          <div className="rounded-lg bg-neutral-50 px-3 py-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-800 text-sm font-bold text-white shadow-sm">
                {user.prenom?.[0]}{user.nom?.[0]}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-neutral-900">{user.prenom}</p>
                <p className="truncate text-sm text-neutral-700">{user.nom}</p>
              </div>
            </div>
          </div>
        )}
        {/* Logout */}
        <button
          onClick={handleLogout}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-900"
        >
          <LogOut className="h-4 w-4" />
          <span>{t('common.logout', 'Déconnexion')}</span>
        </button>
      </div>
    </div>
  )
}
